import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMAGES } from "@/data";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-[85vh] pt-28 pb-10 overflow-hidden bg-background">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
        {/* Text */}
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-extrabold leading-[1.05] text-5xl sm:text-6xl lg:text-[4.4vw] tracking-tight text-primary"
          >
            Trusted <span className="text-accent">dog walking &amp; boarding</span> in South Brisbane
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-7 max-w-md text-lg sm:text-xl text-muted-foreground font-medium"
          >
            Cheeky Pups offer dog walking, boarding, sitting, doggie daycare and
            house visits across South Brisbane.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#book"
              data-testid="hero-book-now"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-bold text-primary-foreground hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/25 transition-all"
            >
              Request a booking
              <ArrowDownRight size={18} className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform" />
            </a>
            <a
              href="#services"
              data-testid="hero-view-services"
              className="inline-flex items-center gap-2 rounded-full border-2 border-primary/20 bg-white px-7 py-4 text-base font-bold text-primary hover:border-accent transition-colors"
            >
              Services &amp; pricing
            </a>
          </motion.div>
        </div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[420px] sm:h-[540px] lg:h-[600px] rounded-[2.5rem] overflow-hidden bg-muted border-4 border-white shadow-[0_25px_60px_-20px_rgba(93,87,107,0.4)]"
          data-testid="hero-image"
        >
          <motion.img
            src={IMAGES.hero}
            alt="A happy dog in South Brisbane"
            fetchPriority="high"
            style={{ y: imgY, scale: 1.12 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </motion.div>
      </div>
    </section>
  );
}
